/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    Spread,
} from 'lexical'
import { $applyNodeReplacement, ElementNode } from 'lexical'

const TableCellHeaderStates = {
    BOTH: 3,
    COLUMN: 2,
    NO_STATUS: 0,
    ROW: 1,
}

type TableCellHeaderState =
    (typeof TableCellHeaderStates)[keyof typeof TableCellHeaderStates]

type SerializedTableCellNode = Spread<
    {
        colSpan?: number
        rowSpan?: number
        headerState: TableCellHeaderState
        width?: number
        backgroundColor?: null | string
    },
    SerializedElementNode
>

/** @noInheritDoc */
class TableCellNodeHeadless extends ElementNode {
    /** @internal */
    __colSpan: number
    /** @internal */
    __rowSpan: number
    /** @internal */
    __headerState: TableCellHeaderState
    /** @internal */
    __width?: number
    /** @internal */
    __backgroundColor: null | string

    constructor(
        headerState = TableCellHeaderStates.NO_STATUS,
        colSpan = 1,
        width?: number,
        key?: NodeKey,
    ) {
        super(key)
        this.__colSpan = colSpan
        this.__rowSpan = 1
        this.__headerState = headerState
        this.__width = width
        this.__backgroundColor = null
    }

    static getType(): string {
        return 'tablecell'
    }

    static clone(node: TableCellNodeHeadless): TableCellNodeHeadless {
        const cellNode = new TableCellNodeHeadless(
            node.__headerState,
            node.__colSpan,
            node.__width,
            node.__key,
        )
        cellNode.__rowSpan = node.__rowSpan
        cellNode.__backgroundColor = node.__backgroundColor
        return cellNode
    }

    static importJSON(
        serializedNode: SerializedTableCellNode,
    ): TableCellNodeHeadless {
        const colSpan = serializedNode.colSpan || 1
        const rowSpan = serializedNode.rowSpan || 1
        const cellNode = $createTableCellNodeHeadless(
            serializedNode.headerState,
            colSpan,
            serializedNode.width || undefined,
        )
        cellNode.__rowSpan = rowSpan
        cellNode.__backgroundColor = serializedNode.backgroundColor || null
        return cellNode
    }
    exportJSON(): SerializedTableCellNode {
        return {
            ...super.exportJSON(),
            backgroundColor: this.getBackgroundColor(),
            colSpan: this.__colSpan,
            headerState: this.__headerState,
            rowSpan: this.__rowSpan,
            type: 'tablecell',
            width: this.getWidth(),
        }
    }

    getColSpan(): number {
        return this.__colSpan
    }

    setColSpan(colSpan: number): this {
        this.getWritable().__colSpan = colSpan
        return this
    }

    getRowSpan(): number {
        return this.__rowSpan
    }

    setRowSpan(rowSpan: number): this {
        this.getWritable().__rowSpan = rowSpan
        return this
    }

    getTag(): string {
        return this.hasHeader() ? 'th' : 'td'
    }

    setHeaderStyles(headerState: TableCellHeaderState): TableCellHeaderState {
        const self = this.getWritable()
        self.__headerState = headerState
        return this.__headerState
    }

    getHeaderStyles(): TableCellHeaderState {
        return this.getLatest().__headerState
    }

    setWidth(width: number): number | null | undefined {
        const self = this.getWritable()
        self.__width = width
        return this.__width
    }

    getWidth(): number | undefined {
        return this.getLatest().__width
    }

    getBackgroundColor(): null | string {
        return this.getLatest().__backgroundColor
    }

    setBackgroundColor(newBackgroundColor: null | string): void {
        this.getWritable().__backgroundColor = newBackgroundColor
    }

    toggleHeaderStyle(
        headerStateToToggle: TableCellHeaderState,
    ): TableCellNodeHeadless {
        const self = this.getWritable()

        if (
            (self.__headerState & headerStateToToggle) ===
            headerStateToToggle
        ) {
            self.__headerState -= headerStateToToggle
        } else {
            self.__headerState += headerStateToToggle
        }

        return self
    }

    hasHeaderState(headerState: TableCellHeaderState): boolean {
        return (this.getHeaderStyles() & headerState) === headerState
    }

    hasHeader(): boolean {
        return (
            this.getLatest().__headerState !== TableCellHeaderStates.NO_STATUS
        )
    }
}

function $createTableCellNodeHeadless(
    headerState: TableCellHeaderState,
    colSpan = 1,
    width?: number,
): TableCellNodeHeadless {
    return $applyNodeReplacement(
        new TableCellNodeHeadless(headerState, colSpan, width),
    )
}

function $isTableCellNodeHeadless(
    node: LexicalNode | null | undefined,
): node is TableCellNodeHeadless {
    return node instanceof TableCellNodeHeadless
}

export {
    TableCellHeaderStates,
    type TableCellHeaderState,
    type SerializedTableCellNode,
    $createTableCellNodeHeadless,
    $isTableCellNodeHeadless,
    TableCellNodeHeadless,
}
